import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { TicketStatus } from '@kfolks/types'
import { Badge } from '@/shared/components/atoms/ui/badge'
import { Button } from '@/shared/components/atoms/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/atoms/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/atoms/ui/table'
import type { TicketDTO } from '../api/tickets.api'
import { useAdminTickets } from '../hooks/useAdminTickets'
import { ticketsKeys } from '../hooks/query-keys'
import { type SyncJobState, useSyncJob } from '../hooks/useSyncJob'

/**
 * Admin Tickets page — cross-request view of every ticket the platform
 * has emitted (Spec 3 follow-up). Replicates the per-request
 * `<TicketsSection>` "Sync now" affordance at the top so admins can
 * force a Productive poll without waiting for the 6-hour cron.
 *
 * Filters: status (open / closed). Pagination is server-side.
 *
 * Visibility: admin-only; the route layer wraps in `<RequireRole>` and
 * the backend enforces the same role gate. Non-admins land on /forbidden.
 */

const PAGE_SIZE = 50

export function AdminTicketsPage() {
	const queryClient = useQueryClient()
	const [status, setStatus] = useState<TicketStatus | 'all'>('all')
	const [page, setPage] = useState(1)

	const listQuery = useAdminTickets({ status, page, limit: PAGE_SIZE })
	const sync = useSyncJob(() => {
		void queryClient.invalidateQueries({ queryKey: ticketsKeys.adminList({ status, page }) })
	})

	const tickets = listQuery.data?.data ?? []
	const total = listQuery.data?.total ?? 0
	const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<h1 className="text-xl font-semibold">Tickets</h1>
					<p className="text-sm text-muted-foreground">
						Every ticket Flight Control has emitted to an external ticketing system. The cron syncs rows older than 6
						hours; "Sync now" force-polls every open ticket regardless of when it was last seen. Closed rows are skipped
						either way — re-opens in Productive aren't reflected back.
					</p>
				</div>
				<SyncBar sync={sync} />
			</div>

			<Card>
				<CardHeader>
					<TicketsHeader
						status={status}
						onStatusChange={(next) => {
							setStatus(next)
							setPage(1)
						}}
					/>
				</CardHeader>
				<CardContent>
					<TicketsBody
						isLoading={listQuery.isLoading}
						isError={listQuery.isError}
						tickets={tickets}
						page={page}
						lastPage={lastPage}
						total={total}
						onPageChange={setPage}
					/>
				</CardContent>
			</Card>
		</div>
	)
}

function SyncBar({ sync }: { sync: SyncJobState }) {
	return (
		<div className="flex items-center gap-2 text-xs">
			{sync.pendingJobId ? (
				<SyncStatusBadge status={sync.jobStatus?.status} error={sync.jobStatus?.error ?? null} />
			) : null}
			<Button
				type="button"
				variant="outline"
				size="sm"
				disabled={sync.triggerPending}
				onClick={sync.trigger}
				data-testid="admin-tickets-sync-trigger"
			>
				{sync.triggerPending ? 'Queueing…' : 'Sync now'}
			</Button>
		</div>
	)
}

function SyncStatusBadge({ status, error }: { status?: string; error?: string | null }) {
	if (!status) return <span className="text-muted-foreground">Polling…</span>
	if (status === 'failed')
		return (
			<span title={error ?? undefined} className="text-destructive">
				Sync failed
			</span>
		)
	if (status === 'completed') return <span className="text-green-600">Sync complete</span>
	if (status === 'running') return <span className="text-muted-foreground">Sync running…</span>
	return <span className="text-muted-foreground">Sync queued</span>
}

// ─── Header + body ───────────────────────────────────────────────────────────

function TicketsHeader({
	status,
	onStatusChange,
}: {
	status: TicketStatus | 'all'
	onStatusChange: (next: TicketStatus | 'all') => void
}) {
	return (
		<div className="flex flex-wrap items-center justify-between gap-3">
			<CardTitle className="text-base">All tickets</CardTitle>
			<div className="flex items-center gap-2 text-xs">
				<label htmlFor="ticket-status-filter">Status:</label>
				<select
					id="ticket-status-filter"
					className="rounded border border-input bg-background px-2 py-1 text-sm"
					value={status}
					onChange={(e) => onStatusChange(e.target.value as TicketStatus | 'all')}
				>
					<option value="all">All</option>
					<option value={TicketStatus.OPEN}>Open</option>
					<option value={TicketStatus.CLOSED}>Closed</option>
				</select>
			</div>
		</div>
	)
}

function TicketsBody({
	isLoading,
	isError,
	tickets,
	page,
	lastPage,
	total,
	onPageChange,
}: {
	isLoading: boolean
	isError: boolean
	tickets: TicketDTO[]
	page: number
	lastPage: number
	total: number
	onPageChange: (next: number) => void
}) {
	if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>
	if (isError) return <p className="text-sm text-muted-foreground">Failed to load tickets.</p>
	if (tickets.length === 0) return <p className="text-sm text-muted-foreground">No tickets found.</p>
	return (
		<>
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Title</TableHead>
						<TableHead>Status</TableHead>
						<TableHead>External status</TableHead>
						<TableHead>Source</TableHead>
						<TableHead>External system</TableHead>
						<TableHead>External</TableHead>
						<TableHead>Request</TableHead>
						<TableHead>Last synced</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{tickets.map((ticket) => (
						<TicketTableRow key={ticket.ticketId} ticket={ticket} />
					))}
				</TableBody>
			</Table>
			<Pager page={page} lastPage={lastPage} total={total} onPageChange={onPageChange} />
		</>
	)
}

function TicketTableRow({ ticket }: { ticket: TicketDTO }) {
	const lastSynced = ticket.lastSyncedAt ? new Date(ticket.lastSyncedAt) : null
	const lastSyncedStr = lastSynced && !Number.isNaN(lastSynced.getTime()) ? lastSynced.toLocaleString() : '—'
	// `externalStatusLastSeen` is the external system's numeric workflow
	// status id, anchored on every sync (see ticket-poll-diff.ts). Today
	// only Productive populates it; we surface it raw because Productive's
	// workflow_status_id is per-project — without a per-project lookup
	// table we don't have a label to show. Future work: hydrate via the
	// Productive `workflow_statuses` endpoint and render the name.
	const externalStatusId = (ticket.metadata as { externalStatusLastSeen?: number | null } | null)
		?.externalStatusLastSeen
	return (
		<TableRow data-testid="admin-ticket-row">
			<TableCell className="font-medium">{ticket.title}</TableCell>
			<TableCell>
				<Badge variant={ticket.status === TicketStatus.CLOSED ? 'outline' : 'secondary'}>{ticket.status}</Badge>
			</TableCell>
			<TableCell
				className="text-xs text-muted-foreground"
				title="Productive workflow_status_id (per-project numeric id)"
			>
				{typeof externalStatusId === 'number' ? `#${externalStatusId}` : '—'}
			</TableCell>
			<TableCell className="text-xs text-muted-foreground">{ticket.source}</TableCell>
			<TableCell className="text-xs text-muted-foreground">{ticket.externalSystem}</TableCell>
			<TableCell>
				{ticket.externalTicketUrl ? (
					<a
						href={ticket.externalTicketUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="text-xs text-primary underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					>
						{ticket.externalTicketId ?? 'Open ↗'} ↗
					</a>
				) : (
					<span className="text-xs text-muted-foreground">—</span>
				)}
			</TableCell>
			<TableCell className="text-xs">
				{ticket.requestId ? (
					<Link
						to={`/requests-center/${ticket.requestId}`}
						className="text-primary underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					>
						View
					</Link>
				) : (
					<span className="text-muted-foreground">—</span>
				)}
			</TableCell>
			<TableCell className="text-xs text-muted-foreground">{lastSyncedStr}</TableCell>
		</TableRow>
	)
}

function Pager({
	page,
	lastPage,
	total,
	onPageChange,
}: {
	page: number
	lastPage: number
	total: number
	onPageChange: (next: number) => void
}) {
	return (
		<div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
			<span>
				Page {page} of {lastPage} · {total} total
			</span>
			<div className="flex gap-2">
				<Button
					type="button"
					variant="outline"
					size="sm"
					disabled={page <= 1}
					onClick={() => onPageChange(Math.max(1, page - 1))}
				>
					Previous
				</Button>
				<Button
					type="button"
					variant="outline"
					size="sm"
					disabled={page >= lastPage}
					onClick={() => onPageChange(page + 1)}
				>
					Next
				</Button>
			</div>
		</div>
	)
}
